import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateTorus } from '@babylonjs/core/Meshes/Builders/torusBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import '@babylonjs/core/Layers/effectLayerSceneComponent';

import { PALETTE } from '@observant/shared';
import type { BallRoomState } from '../sim/ballroom.js';
import { createBackground, type Background } from './background.js';

const PIVOT_X = 2; // half the rest gap
const PIVOT_Y = 5.8;
const CHAIN_LEN = 2.9; // matches DEFAULT_BALLROOM_CONFIG.chainLen

function metal(scene: Scene): StandardMaterial {
  const m = new StandardMaterial('silver', scene);
  m.diffuseColor = new Color3(0.55, 0.6, 0.66);
  m.specularColor = new Color3(1, 1, 1);
  m.specularPower = 96;
  m.emissiveColor = new Color3(0.04, 0.05, 0.07);
  return m;
}

export class BallRoomScene {
  readonly engine: Engine;
  readonly scene: Scene;
  private camera: UniversalCamera;
  private background: Background;
  private pivotL: TransformNode;
  private pivotR: TransformNode;
  private ballL: ReturnType<typeof CreateSphere>;
  private ballR: ReturnType<typeof CreateSphere>;
  private arc: ReturnType<typeof CreateBox>;
  private arcLight: PointLight;
  private wheel: ReturnType<typeof CreateTorus>;
  private arcMat: StandardMaterial;
  private blast: ReturnType<typeof CreateSphere>;
  private blastMat: StandardMaterial;

  constructor(engine: Engine) {
    this.engine = engine;
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.012, 0.02, 0.05, 1);

    this.camera = new UniversalCamera('cam', new Vector3(0, 3.2, -9.5), this.scene);
    this.camera.setTarget(new Vector3(0, 3.0, 0));
    this.camera.fov = 0.9;
    this.camera.minZ = 0.1;
    this.camera.maxZ = 600;

    const glow = new GlowLayer('glow', this.scene, { blurKernelSize: 24 });
    glow.intensity = 0.8;

    const hemi = new HemisphericLight('hemi', new Vector3(0, 1, -0.3), this.scene);
    hemi.intensity = 0.55;
    hemi.diffuse = new Color3(0.7, 0.8, 1);
    const key = new PointLight('key', new Vector3(0, 6, -6), this.scene);
    key.intensity = 0.6;

    this.background = createBackground(this.scene);

    const silver = metal(this.scene);
    const dark = new StandardMaterial('crane', this.scene);
    dark.diffuseColor = new Color3(0.08, 0.1, 0.14);
    dark.emissiveColor = new Color3(0.02, 0.03, 0.05);

    // Cranes (vertical post + arm) and chained balls on each side.
    for (const sx of [-1, 1]) {
      const post = CreateBox(
        `post-${sx}`,
        { width: 0.3, height: PIVOT_Y + 1, depth: 0.3 },
        this.scene,
      );
      post.position.set(sx * 3.4, (PIVOT_Y + 1) / 2, 0.4);
      post.material = dark;
      const arm = CreateBox(`arm-${sx}`, { width: 1.8, height: 0.25, depth: 0.25 }, this.scene);
      arm.position.set(sx * 2.7, PIVOT_Y, 0.4);
      arm.material = dark;
    }

    const makeChainBall = (px: number) => {
      const pivot = new TransformNode(`pivot-${px}`, this.scene);
      pivot.position.set(px, PIVOT_Y, 0);
      const chain = CreateCylinder(
        `chain-${px}`,
        { diameter: 0.08, height: CHAIN_LEN },
        this.scene,
      );
      chain.parent = pivot;
      chain.position.set(0, -CHAIN_LEN / 2, 0);
      chain.material = dark;
      const ball = CreateSphere(`ball-${px}`, { diameter: 1.4, segments: 24 }, this.scene);
      ball.parent = pivot;
      ball.position.set(0, -CHAIN_LEN, 0);
      ball.material = silver;
      return { pivot, ball };
    };
    const left = makeChainBall(-PIVOT_X);
    const right = makeChainBall(PIVOT_X);
    this.pivotL = left.pivot;
    this.pivotR = right.pivot;
    this.ballL = left.ball;
    this.ballR = right.ball;

    // Electric arc between the balls (flickers when attracting).
    this.arcMat = new StandardMaterial('arc', this.scene);
    this.arcMat.emissiveColor = new Color3(0.6, 0.95, 1);
    this.arcMat.disableLighting = true;
    this.arc = CreateBox('arc', { width: 1, height: 0.08, depth: 0.08 }, this.scene);
    this.arc.material = this.arcMat;
    this.arc.setEnabled(false);
    this.arcLight = new PointLight('arcLight', new Vector3(0, PIVOT_Y - CHAIN_LEN, 0), this.scene);
    this.arcLight.diffuse = new Color3(0.6, 0.9, 1);
    this.arcLight.intensity = 0;

    // The voltage wheel, front-and-center (mirrors the DOM control).
    this.wheel = CreateTorus(
      'wheel',
      { diameter: 1.6, thickness: 0.28, tessellation: 24 },
      this.scene,
    );
    this.wheel.position.set(0, 1.0, -6.4);
    this.wheel.rotation.x = Math.PI / 2;
    const wheelMat = new StandardMaterial('wheelMat', this.scene);
    wheelMat.emissiveColor = Color3.FromHexString(PALETTE.neonCyan).scale(0.5);
    wheelMat.disableLighting = true;
    this.wheel.material = wheelMat;

    // Explosion flash sphere (hidden until the balls collide).
    this.blastMat = new StandardMaterial('blast', this.scene);
    this.blastMat.emissiveColor = new Color3(1, 0.9, 0.7);
    this.blastMat.disableLighting = true;
    this.blast = CreateSphere('blast', { diameter: 1, segments: 16 }, this.scene);
    this.blast.material = this.blastMat;
    this.blast.setEnabled(false);

    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
  }

  private onResize(): void {
    this.engine.resize();
  }

  sync(state: BallRoomState): void {
    const ballY = PIVOT_Y - Math.cos(state.theta) * CHAIN_LEN;

    if (state.broken) {
      // Explosion: blast flash expands+fades; balls fling apart and fall.
      const e = state.t - state.tBreak;
      this.arc.setEnabled(false);
      this.blast.setEnabled(e < 0.7);
      this.blast.position.set(0, ballY, 0);
      const bs = 0.6 + e * 22;
      this.blast.scaling.set(bs, bs, bs);
      this.blast.visibility = Math.max(0, 1 - e / 0.6);
      this.blastMat.emissiveColor = new Color3(
        1,
        Math.max(0.2, 0.9 - e),
        Math.max(0, 0.7 - e * 1.5),
      );
      this.arcLight.position.y = ballY;
      this.arcLight.intensity = Math.max(0, 8 - e * 16);
      // Fling the pendulums outward and down.
      this.pivotL.rotation.z = state.theta - e * 1.5;
      this.pivotR.rotation.z = -state.theta + e * 1.5;
      this.pivotL.position.set(-PIVOT_X - e * 5, PIVOT_Y - e * e * 9, 0);
      this.pivotR.position.set(PIVOT_X + e * 5, PIVOT_Y - e * e * 9, 0);
    } else {
      // Swing the pendulums (inward = +theta).
      this.pivotL.rotation.z = state.theta;
      this.pivotR.rotation.z = -state.theta;
      const gap = Math.max(0.2, 2 * PIVOT_X - 2 * CHAIN_LEN * Math.sin(state.theta));
      if (state.attracting) {
        this.arc.setEnabled(true);
        this.arc.position.set(0, ballY, 0);
        this.arc.scaling.x = gap;
        const flicker = 0.5 + Math.random() * 0.5;
        this.arc.visibility = flicker;
        this.arcMat.emissiveColor = new Color3(0.6 * flicker, 0.95 * flicker, flicker);
        this.arcLight.position.y = ballY;
        this.arcLight.intensity = 1.5 * flicker;
      } else {
        this.arc.setEnabled(false);
        this.arcLight.intensity = 0;
      }
    }

    // Wheel spins with the voltage.
    this.wheel.rotation.y = state.t * (0.5 + state.voltage * 6);

    // Idle camera drift.
    this.camera.position.x = Math.sin(state.t * 0.3) * 0.5;

    // Animated chamber backdrop so the room isn't a black void.
    this.background.mesh.position.set(this.camera.position.x, 4, 70);
    const aspect = this.engine.getRenderWidth() / Math.max(1, this.engine.getRenderHeight());
    this.background.update(state.t, aspect);
  }

  render(): void {
    this.scene.render();
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    this.scene.dispose(); // engine is shared/owned by main — do not dispose it
  }
}
